import type { EventCalendarEvent } from "@/components/cinch/EventCalendar";

import type { CalendarEvent, EventKind } from "./api";

export const KIND_LABEL: Record<EventKind, string> = {
  payroll: "Payroll",
  headcount: "Headcount",
  vendor: "Vendor",
  new_revenue: "New contract",
  churn: "Churn",
  one_off: "One-off",
};

/**
 * Chip tone per kind, in semantic tokens only — no raw Tailwind palette
 * colours (the `.tsx` lint rule bans those, and this file shouldn't dodge it
 * just because it's a `.ts`). Inflows read green, outflows by category.
 */
export const KIND_CLASS: Record<EventKind, string> = {
  payroll: "bg-brand/10 text-brand border-brand/30",
  headcount: "bg-power/10 text-power border-power/30",
  vendor: "bg-warning-light text-warning border-warning/30",
  new_revenue: "bg-success-light text-success border-success/30",
  churn: "bg-danger/10 text-danger border-danger/30",
  one_off: "bg-muted text-foreground border-border",
};

/**
 * How firm an assumption is, as a visual weight. Speculative reads faint on
 * purpose — it shouldn't carry the same authority as a signed fact, and the
 * cash engine doesn't act on it either.
 */
export const STATE_LABEL: Record<string, string> = {
  speculative: "Speculative",
  offer_accepted: "Offer accepted",
  started: "Started",
  baked_in: "Baked in",
  notice_given: "Notice given",
  confirmed: "Confirmed",
  expected: "Expected",
  committed: "Committed",
};

export function stateClass(state: string | null): string {
  if (state === "speculative") return "opacity-60 border-dashed";
  if (state === "baked_in") return "opacity-75";
  return "";
}

/** Short currency for a calendar chip: -$53.6K. */
export function chipAmount(amount: number): string {
  if (!amount) return "";
  const abs = Math.abs(amount);
  const sign = amount < 0 ? "-" : "+";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}K`;
  return `${sign}$${Math.round(abs)}`;
}

/**
 * Map a forecast assumption onto the shared `EventCalendar` shape.
 *
 * The cash date is the SOLID one — that's when money moves and what the engine
 * consumes. The effective date rides along as `linkedDate`, so the calendar
 * draws the gap between the two rather than picking one.
 */
export function toCalendarEvent(e: CalendarEvent): EventCalendarEvent {
  const label = KIND_LABEL[e.kind] ?? e.kind;
  const state = e.state ? (STATE_LABEL[e.state] ?? e.state) : null;
  const money = chipAmount(e.amount);
  return {
    id: e.id,
    title: e.title,
    date: e.cash_date,
    linkedDate: e.effective_date,
    trailing: money,
    chipClassName: cnKind(e),
    tooltip: `${label}: ${e.title}${state ? ` (${state})` : ""}${money ? ` · ${money}` : ""}`,
    linkedTooltip: `${label}: ${e.title} — happens today, cash moves ${e.cash_date}`,
  };
}

function cnKind(e: CalendarEvent): string {
  const base = KIND_CLASS[e.kind] ?? "border-border bg-muted";
  const state = stateClass(e.state);
  return state ? `${base} ${state}` : base;
}

/** Which assumption table a calendar event id belongs to. Ids are
 *  "kind:uuid", so the kind survives the round trip to the drawer. */
export function eventEntityId(id: string): { kind: string; entityId: string } {
  const at = id.indexOf(":");
  return at < 0
    ? { kind: "", entityId: id }
    : { kind: id.slice(0, at), entityId: id.slice(at + 1) };
}
